import type {
  NewReaction as PluginReaction,
  Operator as PluginOperator,
  ReactionModifier as PluginReactionModifier,
  ReactionProduct as PluginReactionProduct,
} from 'minerva-plugin-api';
import { NewReaction } from '@/types/models';
import { toPluginLine } from '@/services/pluginsManager/adapters/toPluginModelElement';
import { toPluginAnnotations } from '@/services/pluginsManager/adapters/toPluginAnnotation';

type ReactionProduct = NewReaction['products'][number];
type ReactionModifier = NewReaction['modifiers'][number];
type Operator = NewReaction['operators'][number];

const toPluginReactionProduct = (product: ReactionProduct): PluginReactionProduct => ({
  ...product,
  line: toPluginLine(product.line),
});

const toPluginReactionModifier = (modifier: ReactionModifier): PluginReactionModifier => ({
  ...modifier,
  line: toPluginLine(modifier.line),
});

const toPluginOperator = (operator: Operator): PluginOperator => ({
  ...operator,
  line: toPluginLine(operator.line),
});

/**
 * Properties the VMH/Recon annotator used to populate. It was removed from minerva,
 * so nothing fills them any more, but plugins are still given them empty.
 */
const droppedVmhProperties = (): Pick<
  PluginReaction,
  | 'abbreviation'
  | 'formula'
  | 'geneProteinReaction'
  | 'lowerBound'
  | 'mechanicalConfidenceScore'
  | 'subsystem'
  | 'symbol'
  | 'synonyms'
  | 'upperBound'
> => ({
  abbreviation: null,
  formula: null,
  geneProteinReaction: null,
  lowerBound: null,
  mechanicalConfidenceScore: null,
  subsystem: null,
  symbol: null,
  synonyms: [],
  upperBound: null,
});

/**
 * The API returns the identifier from the source file as reactionId, but plugins
 * were always given it as idReaction.
 */
export const toPluginReaction = ({ reactionId, ...reaction }: NewReaction): PluginReaction => ({
  ...droppedVmhProperties(),
  ...reaction,
  idReaction: reactionId,
  model: reaction.map,
  line: toPluginLine(reaction.line),
  products: reaction.products.map(toPluginReactionProduct),
  reactants: reaction.reactants.map(toPluginReactionProduct),
  modifiers: reaction.modifiers.map(toPluginReactionModifier),
  operators: reaction.operators.map(toPluginOperator),
  references: toPluginAnnotations(reaction.references),
});
